import React, { useState, useEffect } from 'react';
import { getServices, updateService } from '../services/api';

export default function EditServiceModal({ serviceId, onClose, onUpdated }) {
  const [form, setForm] = useState({
    name: '', category: '', check_url: '',
    check_method: 'GET', timeout_ms: 5000, sla_target: 99.9, active: true,
  });
  const [categories, setCategories] = useState([]);
  const [errors,   setErrors]   = useState({});
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    getServices()
      .then(res => {
        const list = res?.data?.data || [];
        const unique = [...new Set(list.map(s => s.category).filter(Boolean))].sort();
        setCategories(unique);
        const service = list.find(s => String(s.id) === String(serviceId));
        if (!service) { setApiError('Service introuvable'); return; }
        setForm({
          name:         service.name         || '',
          category:     service.category     || '',
          check_url:    service.check_url    || '',
          check_method: service.check_method || 'GET',
          timeout_ms:   service.timeout_ms   || 5000,
          sla_target:   service.sla_target   || 99.9,
          active:       service.active       ?? true,
        });
      })
      .catch(() => setApiError('Impossible de charger le service'))
      .finally(() => setLoading(false));
  }, [serviceId]);

  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())      e.name      = 'Le nom est requis';
    if (!form.category.trim())  e.category  = 'La catégorie est requise';
    if (!form.check_url.trim()) e.check_url = "L'URL est requise";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setApiError('');
    try {
      const res = await updateService(serviceId, form);
      onUpdated(res.data.data || res.data);
    } catch (err) {
      setApiError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#64748B',
                       textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 };
  const inputStyle = (hasError) => ({
    width: '100%', padding: '9px 12px',
    border: `1px solid ${hasError ? '#DC2626' : '#E2E8F0'}`,
    borderRadius: 8, fontSize: 13, outline: 'none',
    background: '#F8FAFC', color: '#1B2A4A', boxSizing: 'border-box',
  });
  const errMsg = { color: '#DC2626', fontSize: 11, marginTop: 3 };
  const field  = { marginBottom: 16 };

  if (loading) return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 32,
                  width: '100%', maxWidth: 560, textAlign: 'center',
                  color: '#64748B', fontSize: 14 }}>
      Chargement…
    </div>
  );

  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: 32,
      width: '100%', maxWidth: 560, maxHeight: '90vh',
      overflowY: 'auto', boxSizing: 'border-box', position: 'relative',
      boxShadow: '0 20px 60px rgba(0,0,0,.3)',
    }}>
      {/* Fermeture */}
      <button type="button" onClick={onClose} style={{
        position: 'absolute', top: 16, right: 16, width: 32, height: 32,
        borderRadius: 8, background: '#F1F5F9', border: 'none',
        fontSize: 18, cursor: 'pointer', color: '#64748B',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>×</button>

      {/* Header */}
      <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #F1F5F9' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1B2A4A' }}>✏️ Modifier le service</div>
        <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
          Mise à jour des paramètres du service
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* NOM */}
        <div style={field}>
          <label style={labelStyle}>Nom *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            style={inputStyle(errors.name)} />
          {errors.name && <div style={errMsg}>{errors.name}</div>}
        </div>

        {/* CATÉGORIE */}
        <div style={field}>
          <label style={labelStyle}>Catégorie *</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" value={form.category}
              onChange={e => set('category', e.target.value)}
              style={{ ...inputStyle(errors.category), flex: 1 }} />
            {categories.length > 0 && (
              <select value="" onChange={e => { if (e.target.value) set('category', e.target.value); }}
                style={{ ...inputStyle(false), width: 'auto', flexShrink: 0,
                         cursor: 'pointer', appearance: 'none',
                         paddingRight: 28, color: '#64748B', fontSize: 12 }}>
                <option value="">Existantes…</option>
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            )}
          </div>
          {errors.category && <div style={errMsg}>{errors.category}</div>}
        </div>

        {/* URL */}
        <div style={field}>
          <label style={labelStyle}>URL *</label>
          <input value={form.check_url} onChange={e => set('check_url', e.target.value)}
            style={inputStyle(errors.check_url)} />
          {errors.check_url && <div style={errMsg}>{errors.check_url}</div>}
        </div>

        {/* MÉTHODE */}
        <div style={field}>
          <label style={labelStyle}>Méthode HTTP</label>
          <select value={form.check_method} onChange={e => set('check_method', e.target.value)}
            style={{ ...inputStyle(false), cursor: 'pointer', appearance: 'none', paddingRight: 32 }}>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="HEAD">HEAD</option>
          </select>
        </div>

        {/* TIMEOUT + SLA */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Timeout (ms)</label>
            <input type="number" min={100} max={30000} value={form.timeout_ms}
              onChange={e => set('timeout_ms', +e.target.value)} style={inputStyle(false)} />
          </div>
          <div>
            <label style={labelStyle}>Objectif SLA (%)</label>
            <input type="number" min={0} max={100} step={0.1} value={form.sla_target}
              onChange={e => set('sla_target', +e.target.value)} style={inputStyle(false)} />
          </div>
        </div>

        {/* ACTIF */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <input type="checkbox" id="active-edit" checked={form.active}
            onChange={e => set('active', e.target.checked)}
            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#2563EB' }} />
          <label htmlFor="active-edit" style={{ fontSize: 13, color: '#1B2A4A',
                                                fontWeight: 500, cursor: 'pointer' }}>
            Service actif
          </label>
        </div>

        {apiError && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
                        padding: '10px 14px', color: '#DC2626', fontSize: 12, marginBottom: 16 }}>
            ❌ {apiError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end',
                      paddingTop: 16, borderTop: '1px solid #F1F5F9' }}>
          <button type="button" onClick={onClose}
            style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #E2E8F0',
                     background: '#fff', fontSize: 13, cursor: 'pointer', color: '#64748B' }}>
            Annuler
          </button>
          <button type="submit" disabled={saving}
            style={{ padding: '9px 24px', borderRadius: 8, border: 'none',
                     background: saving ? '#93C5FD' : '#2563EB', color: '#fff',
                     fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Enregistrement…' : '💾 Modifier'}
          </button>
        </div>
      </form>
    </div>
  );
}